import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Bus, Warehouse, ChevronDown } from "lucide-react";
import { api, fmtDec, fmtInt } from "../lib/api";

const COLOR_DEAD = "#9CA3AF";

// Paleta cíclica de colores por línea
const LINE_PALETTE = [
  "#0EA5E9", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6",
  "#06B6D4", "#EC4899", "#84CC16", "#F97316", "#14B8A6",
  "#6366F1", "#A855F7", "#22C55E", "#EAB308", "#DC2626",
  "#2563EB", "#D946EF",
];

function colorFor(numeroLinea, allLineas) {
  const idx = allLineas.indexOf(numeroLinea);
  return LINE_PALETTE[idx % LINE_PALETTE.length];
}

export default function PlanificacionFlota() {
  const [lotes, setLotes] = useState([]);
  const [lote, setLote] = useState(null);
  const [combinaciones, setCombinaciones] = useState([]);
  const [tipoDia, setTipoDia] = useState(null);
  const [temporada, setTemporada] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadLotes = useCallback(async (cancelledRef) => {
    const r = await api.get("/lotes");
    if (cancelledRef.current) return;
    const ls = (r.data || []).map((l) => l.lote);
    setLotes(ls);
    setLote((current) => current || ls[0] || null);
  }, []);

  const loadCombinaciones = useCallback(async (loteX, cancelledRef) => {
    const r = await api.get(`/planificacion-flota/combinaciones`, { params: { lote: loteX } });
    if (cancelledRef.current) return;
    const cs = r.data.combinaciones || [];
    setCombinaciones(cs);
    if (cs.length) {
      setTipoDia(cs[0].tipo_dia);
      setTemporada(cs[0].temporada_raw);
    }
  }, []);

  const loadPlan = useCallback(async (loteX, td, tr, cancelledRef) => {
    setLoading(true);
    try {
      const r = await api.get(`/planificacion-flota`, { params: { lote: loteX, tipo_dia: td, temporada_raw: tr } });
      if (!cancelledRef.current) setPlan(r.data);
    } catch {
      if (!cancelledRef.current) setPlan(null);
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ref = { current: false };
    loadLotes(ref);
    return () => { ref.current = true; };
  }, [loadLotes]);

  useEffect(() => {
    if (!lote) return undefined;
    const ref = { current: false };
    setCombinaciones([]); setTipoDia(null); setTemporada(null); setPlan(null);
    loadCombinaciones(lote, ref);
    return () => { ref.current = true; };
  }, [lote, loadCombinaciones]);

  useEffect(() => {
    if (!lote || !tipoDia || !temporada) return undefined;
    const ref = { current: false };
    loadPlan(lote, tipoDia, temporada, ref);
    return () => { ref.current = true; };
  }, [lote, tipoDia, temporada, loadPlan]);

  const { startMin, endMin } = useMemo(() => {
    if (!plan?.vehiculos?.length) return { startMin: 5 * 60, endMin: 26 * 60 };
    const starts = plan.vehiculos.map((v) => v.start_min);
    const ends = plan.vehiculos.map((v) => v.end_min);
    return { startMin: Math.max(0, Math.min(...starts) - 10), endMin: Math.max(...ends) + 10 };
  }, [plan]);

  const totalMin = Math.max(60, endMin - startMin);
  const hourTicks = [];
  for (let h = Math.ceil(startMin / 60); h * 60 <= endMin; h++) hourTicks.push(h);

  const tiposDiaUnique = [...new Set(combinaciones.map((c) => c.tipo_dia))];
  const temporadasUnique = [...new Set(combinaciones.filter((c) => c.tipo_dia === tipoDia).map((c) => c.temporada_raw))];
  const lineasUnique = useMemo(() => {
    if (!plan?.vehiculos) return [];
    return [...new Set(plan.vehiculos.map((v) => v.numero_linea))].sort();
  }, [plan]);

  return (
    <div className="space-y-4" data-testid="planificacion-flota-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Cuadro de horarios · flota completa</div>
        <h1 className="section-title mt-1">Planificación global de la concesión</h1>
        <p className="section-sub">
          Vista agregada: todos los vehículos del lote ordenados por línea, con sus turnos del día. Cada línea tiene su color;
          los segmentos en vacío (cochera ↔ línea o cambios de cabecera) aparecen en gris.
        </p>
      </div>

      {/* Selectores */}
      <div className="crtm-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <SelField label="Concesión" testId="sel-lote">
          <select value={lote || ""} onChange={(e) => setLote(e.target.value)} data-testid="sel-lote-input">
            {lotes.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </SelField>
        <SelField label="Tipo de día" testId="sel-tipodia">
          <select value={tipoDia || ""} onChange={(e) => setTipoDia(e.target.value)} disabled={!tiposDiaUnique.length} data-testid="sel-tipodia-input">
            {tiposDiaUnique.map((td) => <option key={td} value={td}>{td}</option>)}
          </select>
        </SelField>
        <SelField label="Temporada" testId="sel-temporada">
          <select value={temporada || ""} onChange={(e) => setTemporada(e.target.value)} disabled={!temporadasUnique.length} data-testid="sel-temporada-input">
            {temporadasUnique.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </SelField>
      </div>

      {loading && <div className="crtm-card p-6 text-sm text-gray-500" data-testid="loading">Calculando planificación…</div>}

      {!loading && plan && plan.n_vehiculos > 0 && (
        <>
          {/* KPI cards (mismo estilo que el resto de la app) */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="kpis-flota">
            <KpiCard label="Turnos / Vehículos"  value={plan.n_vehiculos}                  hint={`${plan.n_lineas_operadas} líneas operadas`} tone="emerald" />
            <KpiCard label="Km comerciales"      value={`${fmtInt(plan.kpis.km_comercial)} km`} hint="Suma de toda la flota" tone="green" />
            <KpiCard label="Km en vacío"         value={`${fmtInt(plan.kpis.km_vacio)} km`}      hint={`Ratio ${plan.kpis.ratio_vacio_pct}%`} tone="amber" />
            <KpiCard label="Eficiencia vehículos" value={`${plan.kpis.eficiencia_vehiculos_pct}%`} hint="km comercial / km total" tone="blue" />
            <KpiCard label="Utilización media"   value={`${plan.kpis.utilizacion_pct}%`}        hint="h servicio / total" tone="blue" />
            <KpiCard label="Horas totales"       value={`${fmtDec(plan.kpis.horas_total, 0)} h`} hint={`${fmtDec(plan.kpis.horas_servicio, 0)} h servicio`} tone="gray" />
          </div>

          {/* Leyenda de líneas */}
          <div className="crtm-card p-3">
            <div className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mb-2">Líneas operadas ({lineasUnique.length})</div>
            <div className="flex flex-wrap gap-2">
              {lineasUnique.map((n) => (
                <div key={n} className="flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-50 border border-gray-200">
                  <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: colorFor(n, lineasUnique) }} />
                  <span className="text-xs font-mono font-bold text-gray-800">L{n}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Gantt claro (mismo estilo que /planificacion) */}
          <div className="crtm-card overflow-hidden" data-testid="timeline-flota">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-[15px]">
                Concesión {plan.lote} · {plan.tipo_dia} · {plan.temporada_raw}
              </h3>
              {plan.cochera && (
                <span className="text-xs text-gray-500 flex items-center gap-1.5">
                  <Warehouse size={12} /> Cochera: <strong className="text-gray-800">{plan.cochera.nombre}</strong>
                </span>
              )}
            </div>
            <div className="overflow-x-auto max-h-[70vh] overflow-y-auto">
              <div className="min-w-[1100px]">
                {/* Ruler */}
                <div className="flex border-b border-gray-100 sticky top-0 bg-white z-10" style={{ paddingLeft: 200 }}>
                  {hourTicks.map((h) => (
                    <div
                      key={h}
                      style={{ flex: `0 0 ${(60 / totalMin) * 100}%`, textAlign: "left" }}
                      className="text-[11px] text-gray-500 py-2 font-mono border-l border-gray-100 px-1"
                    >
                      {String(h % 24).padStart(2, "0")}:00
                    </div>
                  ))}
                </div>
                {plan.vehiculos.map((v, idx) => (
                  <div
                    key={`${v.display_id}_${v.codigo_linea}_${idx}`}
                    className={"flex items-center border-b border-gray-50 hover:bg-gray-50/60 " + (idx % 2 === 1 ? "bg-gray-50/30" : "")}
                    data-testid={`row-${v.display_id}`}
                  >
                    {/* Stats panel */}
                    <div className="flex-shrink-0 w-[200px] py-1.5 px-3 border-r border-gray-100 text-[10px] font-mono text-gray-700">
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-2 h-4 rounded-sm" style={{ backgroundColor: colorFor(v.numero_linea, lineasUnique) }} />
                        <Bus size={11} className="text-emerald-700" />
                        <span className="font-bold text-gray-900">{v.display_id}</span>
                        <span className="text-gray-400">·</span>
                        <span className="font-bold" style={{ color: colorFor(v.numero_linea, lineasUnique) }}>L{v.numero_linea}</span>
                      </div>
                      <div className="text-[10px] text-gray-500 mt-0.5">
                        {v.n_trips} trips · {fmtInt(v.km_comercial)}+{fmtInt(v.km_vacio)} km · {fmtDec(v.horas_total, 1)} h
                      </div>
                      <div className="text-[10px] text-gray-400">
                        {v.start_label}→{v.end_label} · util {v.utilizacion_pct}%
                      </div>
                    </div>
                    <div className="flex-1 relative h-9">
                      {v.deadheads.map((dh, i) => {
                        const left = ((dh.start_min - startMin) / totalMin) * 100;
                        const width = ((dh.end_min - dh.start_min) / totalMin) * 100;
                        if (width <= 0) return null;
                        return (
                          <div
                            key={`dh-${i}`}
                            className="absolute h-2 rounded-sm"
                            style={{
                              left: `${left}%`, width: `${width}%`, top: "75%",
                              background: `repeating-linear-gradient(45deg, ${COLOR_DEAD}, ${COLOR_DEAD} 3px, ${COLOR_DEAD}66 3px, ${COLOR_DEAD}66 6px)`,
                            }}
                            title={`${dh.tipo} · ${dh.desde} → ${dh.hasta} · ${dh.km} km (${dh.start_label}-${dh.end_label})`}
                          />
                        );
                      })}
                      {v.trips.map((t, i) => {
                        const left = ((t.salida_min - startMin) / totalMin) * 100;
                        const width = ((t.llegada_min - t.salida_min) / totalMin) * 100;
                        if (width <= 0) return null;
                        const color = colorFor(v.numero_linea, lineasUnique);
                        return (
                          <div
                            key={`t-${i}`}
                            className="absolute rounded-sm flex items-center justify-center text-[9px] text-white font-bold border border-white/60 overflow-hidden whitespace-nowrap"
                            style={{
                              left: `${left}%`, width: `${width}%`, top: "12%", height: "55%",
                              backgroundColor: color, minWidth: 4,
                            }}
                            title={`L${v.numero_linea} ${t.sentido} ${t.salida_label}-${t.llegada_label} · ${t.veh_km} km · itin ${t.itinerario}`}
                          >
                            {width > 2.5 && <span className="px-1">{v.numero_linea}</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && plan && plan.n_vehiculos === 0 && (
        <div className="crtm-card p-6 text-sm text-amber-700">No hay expediciones para esta combinación.</div>
      )}
    </div>
  );
}

const SelField = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
    <div className="relative">
      {children}
      <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
    <style>{`
      [data-testid="${testId}"] select {
        width: 100%; padding: 8px 30px 8px 10px;
        border: 1px solid #E5E7EB; border-radius: 6px;
        background: white; font-size: 13px; appearance: none;
      }
      [data-testid="${testId}"] select:focus { outline: none; border-color: #14532D; }
    `}</style>
  </div>
);

const KpiCard = ({ label, value, hint, tone = "gray" }) => {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue:    "bg-blue-50 text-blue-700 border-blue-100",
    green:   "bg-green-50 text-green-700 border-green-100",
    amber:   "bg-amber-50 text-amber-700 border-amber-100",
    gray:    "bg-gray-50 text-gray-700 border-gray-100",
  };
  return (
    <div className={`crtm-card p-3 border ${toneMap[tone]}`}>
      <div className="text-[10px] uppercase font-bold tracking-widest opacity-80">{label}</div>
      <div className="text-lg font-bold mt-1 text-gray-900">{value}</div>
      <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>
    </div>
  );
};
